import _colors from 'styles/global/_colors'
import type { GuidanceType } from '../ComparisonTabs'
import { DataPointSet } from './DataPointIndicator'

const OGM_PRICE_COLOR = _colors.agGridDynamicOGM

export type GuidanceValues = {
  expertValue?: number | null
  typicalValue?: number | null
  floorValue?: number | null
  top25Value?: number | null
  medianValue?: number | null
  bottom25Value?: number | null
}

export type ApprovalValues = Record<string, number | null | undefined>

export type BaseAndRequestedValue = {
  baseline?: number | null
  ogmOrMargin?: number | null
  requestedValue?: number | null
  isRequestedPriceActive: boolean
  guidanceValues?: GuidanceValues
  selectedGuidanceTypes?: GuidanceType[]
  approvalValues?: ApprovalValues
  selectedApprovalTypes?: string[]
  colorMap?: Partial<Record<string, string>>
}

export const createDataPointsValue = ({
  baseline,
  ogmOrMargin,
  requestedValue,
  isRequestedPriceActive,
  guidanceValues,
  selectedGuidanceTypes,
  approvalValues,
  selectedApprovalTypes,
  colorMap,
}: BaseAndRequestedValue): DataPointSet | null => {
  const positions: number[] = []
  const markerColors: string[] = []

  const appendMarker = (value: number | null | undefined, color: string): void => {
    if (value === undefined || value === null) {
      return
    }

    positions.push(value)
    markerColors.push(color)
  }

  appendMarker(baseline, _colors.gray)
  appendMarker(ogmOrMargin, OGM_PRICE_COLOR)

  if (isRequestedPriceActive) {
    appendMarker(requestedValue, colorMap?.requestedPrice ?? _colors.blue)
  }

  // Add guidance markers based on selected guidance types
  if (guidanceValues && selectedGuidanceTypes && selectedGuidanceTypes.length > 0) {
    const guidanceMap: Record<GuidanceType, number | null | undefined> = {
      expert: guidanceValues.expertValue,
      typical: guidanceValues.typicalValue,
      floor: guidanceValues.floorValue,
      top25: guidanceValues.top25Value,
      median: guidanceValues.medianValue,
      bottom25: guidanceValues.bottom25Value,
    }

    selectedGuidanceTypes.forEach((type) => {
      const value = guidanceMap[type]
      appendMarker(value, colorMap?.[type] ?? _colors.gray)
    })
  }

  if (approvalValues && selectedApprovalTypes && selectedApprovalTypes.length > 0) {
    selectedApprovalTypes.forEach((type) => {
      const value = approvalValues[type]
      appendMarker(value, colorMap?.[type] ?? _colors.gray)
    })
  }

  if (!positions.length) {
    return null
  }

  return { positions, markerColors }
}
